import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaLinkedinIn, FaTiktok } from 'react-icons/fa6';
import RamblaLogo from 'assets/img/rambla-logo-black.png';
import { TERMS_OF_SERVICE, PRIVACY_POLICY } from 'constants/routes';
import { media } from 'utils/rwd';

const FooterContainer = styled.footer`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  color: #6c757d;

  ${media.sm.down`
    grid-template-columns: 1fr;
    grid-template-areas:
      "brand"
      "social"
      "legal";
    justify-items: center;
    gap: 10px;
    text-align: center;
  `}
`;

const BrandContainer = styled.div`
  display: inline-flex;
  align-items: center;
  justify-self: start;

  ${media.sm.down`
    grid-area: brand;
    justify-self: center;
  `}
`;

const BrandLogo = styled.img`
  display: block;
  width: 118px;
  height: auto;

  ${media.sm.down`
    width: 104px;
  `}
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;

  ${media.sm.down`
    grid-area: legal;
    gap: 0;
  `}
`;

const FooterColumnRight = styled(FooterColumn)`
  justify-self: end;

  ${media.sm.down`
    grid-area: social;
    justify-self: center;
    gap: 0;
  `}
`;

const FooterHeading = styled.span`
  color: #000;
  font-size: 12px;
  font-weight: bold;
  line-height: 1;
  text-transform: uppercase;

  ${media.sm.down`
    display: none;
  `}
`;

const RegulationsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  font-size: 13px;

  ${media.sm.down`
    gap: 8px;
    font-size: 12px;
  `}

  a {
    color: #6c757d;
    text-decoration: none;

    &:hover {
      color: #000;
      text-decoration: underline;
    }
  }

  span {
    color: #6c757d;
  }
`;

const SocialIcons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  ${media.sm.down`
    justify-content: center;
    gap: 8px;
  `}
`;

const SocialIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: 1px solid #e9ecef;
  border-radius: 50%;
  color: #000;
  text-decoration: none;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  ${media.sm.down`
    width: 30px;
    height: 30px;
  `}

  &:hover,
  &:focus-visible {
    background-color: #be1622;
    border-color: #be1622;
    color: #fff;
  }

  svg {
    width: 14px;
    height: 14px;
  }

  &:hover svg,
  &:focus-visible svg {
    color: #fff;
  }
`;

const SOCIAL_ICONS = [
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/profile.php?id=61566633429421',
    Icon: FaFacebookF,
  },
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/rambla.io_app/',
    Icon: FaInstagram,
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@rambla.io',
    Icon: FaTiktok,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/showcase/ramblaio',
    Icon: FaLinkedinIn,
  },
];

const AuthFooter = () => (
  <FooterContainer>
    <BrandContainer>
      <BrandLogo src={RamblaLogo} alt="Rambla" />
    </BrandContainer>
    <FooterColumn>
      <FooterHeading>Legal</FooterHeading>
      <RegulationsContainer>
        <Link to={TERMS_OF_SERVICE}>Terms of Service</Link>
        <span>•</span>
        <Link to={PRIVACY_POLICY}>Privacy Policy</Link>
      </RegulationsContainer>
    </FooterColumn>
    <FooterColumnRight>
      <FooterHeading>Social</FooterHeading>
      <SocialIcons>
        {SOCIAL_ICONS.map(({ label, href, Icon }) => (
          <SocialIcon
            key={label}
            as="a"
            href={href}
            target="_blank"
            rel="noreferrer noopener"
            aria-label={label}
            title={label}
          >
            <Icon aria-hidden="true" focusable="false" />
          </SocialIcon>
        ))}
      </SocialIcons>
    </FooterColumnRight>
  </FooterContainer>
);

export default AuthFooter;
